import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Clock, AlertCircle, FileText, Upload, Edit2 } from '../../components/icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchProgramDetail, fetchProgramProgressForCreator } from '../../lib/programs';
import { ACTIVITY_TYPE_LABELS } from '../../types/program';
import type { ProgramDetail, ProgramActivityProgress, ProgramActivityResponse } from '../../types/program';

interface Props {
  basePath: string;
}

export function ProgramProgress({ basePath }: Props) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [progressData, setProgressData] = useState<{
    assignment: { id: string; assigneeName: string; assigneeEmail: string; completedAt: string | null; assignedAt: string };
    progress: ProgramActivityProgress[];
    responses: ProgramActivityResponse[];
  }[]>([]);

  useEffect(() => {
    if (!id) return;
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      const [detail, progress] = await Promise.all([
        fetchProgramDetail(id),
        fetchProgramProgressForCreator(id),
      ]);
      setProgram(detail);
      setProgressData(progress.map(p => ({
        assignment: {
          id: p.assignment.id,
          assigneeName: p.assignment.assigneeName || 'Unknown',
          assigneeEmail: p.assignment.assigneeEmail || '',
          completedAt: p.assignment.completedAt,
          assignedAt: p.assignment.assignedAt,
        },
        progress: p.progress,
        responses: p.responses,
      })));
    } catch (err) {
      console.error('Error loading progress:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading progress..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(basePath)} className="text-chocolate-400 hover:text-chocolate-700 text-sm">
            ← Programs
          </button>
          <span className="text-chocolate-300">/</span>
          <h1 className="freud-heading-lg text-chocolate-900 truncate">
            {program?.title || 'Program'} — Progress
          </h1>
          <Link to={`${basePath}/${id}/edit`} className="ml-auto p-2 text-chocolate-400 hover:bg-cream-100 rounded-lg">
            <Edit2 className="w-4 h-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {progressData.length === 0 ? (
          <div className="freud-card-elevated p-12 text-center">
            <div className="w-16 h-16 bg-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-chocolate-300" />
            </div>
            <h2 className="freud-heading-base text-chocolate-900 mb-2">No Assignments Yet</h2>
            <p className="text-sm text-chocolate-500">Assign this program to see recipient progress here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {progressData.map((data, index) => {
              const total = program?.activities.length || 0;
              const completed = data.progress.filter(p => p.status === 'completed').length;
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
              const isComplete = data.assignment.completedAt !== null;

              return (
                <motion.div
                  key={data.assignment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="freud-card-elevated p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center text-sm font-bold">
                        {data.assignment.assigneeName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-chocolate-900">{data.assignment.assigneeName}</h3>
                        <p className="text-xs text-chocolate-400">{data.assignment.assigneeEmail}</p>
                      </div>
                    </div>
                    {isComplete && (
                      <span className="text-xs px-2.5 py-0.5 rounded-full bg-success-50 text-green-700 font-medium">
                        Completed
                      </span>
                    )}
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-chocolate-500 mb-1">
                      <span>{completed} of {total} activities completed</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-cream-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-sage-500 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>

                  {/* Activity breakdown */}
                  <div className="space-y-2">
                    {program?.activities.map((activity, actIndex) => {
                      const prog = data.progress.find(p => p.activityId === activity.id);
                      const status = prog?.status || 'not_started';
                      const response = data.responses.find(r => r.activityId === activity.id);
                      const isOverdue = activity.dueDate && status !== 'completed' && new Date(activity.dueDate) < new Date();

                      return (
                        <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg bg-cream-50">
                          <div className="flex-shrink-0 mt-0.5">
                            {status === 'completed' ? (
                              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                                <Check className="w-3 h-3 text-green-600" />
                              </div>
                            ) : isOverdue ? (
                              <div className="w-5 h-5 rounded-full bg-warm-100 flex items-center justify-center">
                                <AlertCircle className="w-3 h-3 text-warm-600" />
                              </div>
                            ) : status === 'in_progress' ? (
                              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                <Clock className="w-3 h-3 text-blue-600" />
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-cream-300" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-chocolate-400">{actIndex + 1}.</span>
                              <span className="text-xs px-1.5 py-0.5 rounded bg-sage-100 text-sage-700 font-medium">
                                {ACTIVITY_TYPE_LABELS[activity.activityType]}
                              </span>
                              <span className="text-sm text-chocolate-900">{activity.title}</span>
                            </div>
                            {response && response.responseText && (
                              <div className="mt-2 p-2 bg-white rounded border border-cream-200">
                                <p className="text-xs text-chocolate-400 mb-1">Response:</p>
                                <p className="text-xs text-chocolate-700 whitespace-pre-wrap">{response.responseText}</p>
                              </div>
                            )}
                            {response && response.uploadFilename && (
                              <div className="mt-1 flex items-center gap-1 text-xs text-sage-600">
                                <Upload className="w-3 h-3" />
                                {response.uploadFilename}
                              </div>
                            )}
                            {isOverdue && (
                              <span className="text-xs text-warm-600 mt-1 block">Overdue</span>
                            )}
                            {status === 'completed' && prog?.completedAt && (
                              <span className="text-xs text-chocolate-400 mt-0.5 block">
                                Completed: {new Date(prog.completedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
